import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState } from "react";
import { withNamespaces } from "react-i18next";
import TableFilter from "src/components/TableFilter";
import { DDSTypes } from "src/modules/3rd-party-management/constants";
import CommonService from "src/modules/3rd-party-management/apis/common/CommonService";
import { toast } from "react-toastify";
import { isFeatureEnabledForActiveModule } from "src/helpers/module_helper";

const FilteringComponent = ({
    t,
    countries,
    onChange
}) => {
    const [tableFilters, setTableFilters] = useState({
        originCountry: null,
        ddsDeclarationType: null,
        legalEntityIds: [],
        isEudr: null,
        geocledianStatus: null,
        ddsSearch: undefined,
        productName: undefined,
        containerNumber: undefined,
        shipmentNumber: undefined
    });
    const initRef = useRef(false);

    const {
        data: legalEntitiesData,
        isFetching: legalEntitiesFetching
    } = useQuery({
        queryKey: [
            '3rd-party-management-fetch-legal-entities-list'
        ],
        queryFn: async () => {
            return await (CommonService.getInstance()).fetchLegalEntities({
                // hasConnected: true
            });
        },
        cacheTime: 0,
        refetchOnWindowFocus: false,
        enabled: isFeatureEnabledForActiveModule('entity_management') && isFeatureEnabledForActiveModule('eudr_activation'),
        onError: () => {
            toast(t('An error occurred while fetching entities.'), {
                type: 'error'
            });
        }
    });

    const tableFilterTabs = useMemo(() => {
        const items = [];

        if (isFeatureEnabledForActiveModule('eudr_activation')) {
            const ddsTypes = Object.keys(DDSTypes).map((type) => {
                return {
                    id: type,
                    name: t(DDSTypes[type].title)
                }
            });

            items.push({
                type: 'radio_list',
                name: 'originCountry',
                title: 'Country of Origin',
                icon: <i className="ri-global-line" />,
                isLoading: countries.isFetching,
                defaultValue: tableFilters?.originCountry,
                data: countries.data || [],
                searchInListOption: {
                    enabled: true,
                    remote: false
                },
                formatter: (item) => {
                    return item.name
                }
            }, {
                type: 'radio_list',
                name: 'ddsDeclarationType',
                title: t('DDS Declaration Type'),
                icon: <i className="ri-file-list-3-line" />,
                isLoading: countries.isFetching,
                defaultValue: tableFilters?.ddsDeclarationType,
                data: ddsTypes || [],
                searchInListOption: {
                    enabled: true,
                    remote: false
                },
                formatter: (item) => {
                    return item.name
                }
            }, {
                type: 'search_textbox',
                name: 'ddsSearch',
                title: t('DDS Reference Number'),
                icon: <i className="ri-hashtag" />,
                defaultValue: tableFilters?.ddsSearch,
                options: {
                    placeholder: t('Search'),
                    typingDelay: 750
                }
            }, {
                type: 'radio_list',
                name: 'isEudr',
                title: 'EUDR Status',
                icon: <i className="ri-shield-line" />,
                isLoading: false,
                defaultValue: tableFilters?.isEudr,
                data: [
                    {
                        id: 1,
                        title: 'EUDR'
                    },
                    {
                        id: 0,
                        title: 'Non-EUDR'
                    }
                ],
                formatter: (item) => {
                    return item.title
                }
            }, {
                type: 'radio_list',
                name: 'geocledianStatus',
                title: 'GeoCledian Status',
                icon: <i className="ri-refresh-line" />,
                isLoading: false,
                defaultValue: tableFilters?.geocledianStatus,
                data: [
                    {
                        id: 1,
                        title: 'Fetched'
                    },
                    {
                        id: 0,
                        title: 'Not Fetched'
                    }
                ],
                formatter: (item) => {
                    return item.title
                }
            }, {
                type: 'search_textbox',
                name: 'containerNumber',
                title: t('Container Number'),
                icon: <i className="ri-stack-line" />,
                defaultValue: tableFilters?.containerNumber,
                options: {
                    placeholder: t('Search'),
                    typingDelay: 750
                }
            }, {
                type: 'search_textbox',
                name: 'shipmentNumber',
                title: t('Shipment Number'),
                icon: <i className="ri-truck-line" />,
                defaultValue: tableFilters?.shipmentNumber,
                options: {
                    placeholder: t('Search'),
                    typingDelay: 750
                }
            });

            if (isFeatureEnabledForActiveModule('entity_management')) {
                items.push({
                    type: 'check_list',
                    name: 'legalEntityIds',
                    title: 'Legal Entities',
                    icon: <i className="ri-git-branch-line" />,
                    isLoading: legalEntitiesFetching,
                    defaultValue: tableFilters?.legalEntityIds,
                    data: legalEntitiesData?.legalEntities || [],
                    searchInListOption: {
                        enabled: true,
                        remote: false
                    },
                    formatter: (item) => {
                        return item.title
                    }
                })
            }
        }

        const searchPlaceholder = isFeatureEnabledForActiveModule('eudr_activation') ?
            'Search by name or lot number' :
            'Search by name';
        
        items.push({
            type: 'search_textbox',
            name: 'productName',
            title: searchPlaceholder,
            icon: <i className="ri-terminal-box-line" />,
            defaultValue: tableFilters?.productName,
            options: {
                placeholder: searchPlaceholder,
                typingDelay: 750
            },
            displayIndependently: true,
            onFilterChange: (value) => {
                setTableFilters((currentState) => ({ ...currentState, productName: value }))
            }
        });

        return items;
    }, [
        tableFilters,

        countries.isFetching,
        countries.data,

        legalEntitiesData,
        legalEntitiesFetching,
        t
    ]);

    const onTableFilterOkButtonClicked = (filters, independentTabsData) => {
        if (filters) {
            const result = {}

            if ('originCountry' in filters) {
                result.originCountry =
                    filters.originCountry !== 'all' ? filters.originCountry : undefined
            }

            if ('ddsDeclarationType' in filters) {
                result.ddsDeclarationType =
                    filters.ddsDeclarationType !== 'all' ? filters.ddsDeclarationType : undefined
            }

            if ('isEudr' in filters) {
                result.isEudr = filters.isEudr !== 'all' ? filters.isEudr : undefined
            }

            if ('geocledianStatus' in filters) {
                result.geocledianStatus = filters.geocledianStatus !== 'all' ? filters.geocledianStatus : undefined
            }

            if('ddsSearch' in filters) {
                result.ddsSearch = filters.ddsSearch || undefined;
            }

            if('containerNumber' in filters) {
                result.containerNumber = filters.containerNumber || undefined;
            }

            if('shipmentNumber' in filters) {
                result.shipmentNumber = filters.shipmentNumber || undefined;
            }

            result.legalEntityIds = filters.legalEntityIds?.length > 0 ? filters.legalEntityIds : undefined

            setTableFilters({
                ...tableFilters,
                ...result
            })
        } else {
            setTableFilters({
                originCountry: null,
                ddsDeclarationType: null,
                isEudr: null,
                geocledianStatus: null,
                ddsSearch: undefined,
                containerNumber: undefined,
                shipmentNumber: undefined,
                legalEntityIds: [],
                productName: independentTabsData?.productName
            })
        }
    }

    useEffect(() => {
        if (!initRef.current) {
            initRef.current = true;
            return;
        }

        onChange && onChange(tableFilters)
    }, [
        tableFilters,
        onChange
    ]);

    return (
        <TableFilter
            className="me-2"
            defaultActiveTab='originCountry'
            tabs={tableFilterTabs}
            onOkButtonClicked={
                onTableFilterOkButtonClicked
            }
        />
    )
}

export default withNamespaces()(FilteringComponent);